import { createContext, useCallback, useContext, useState } from 'react';
import InsufficientEntitlementsModal from '@components/workspace/InsufficientEntitlementsModal';
import CapLimitReachedModal from '@components/workspace/CapLimitReachedModal';
import { useWorkspaceUsage } from '@hooks/getQueries';
import { WorkspaceContext } from './WorkspaceContext';

const EntitlementContext = createContext();

/**
 * Provider for managing entitlement error modals globally.
 * Wrap this around the main app to enable showing insufficient entitlements
 * modals from anywhere when API calls return 402 errors.
 */
function EntitlementProvider({ children }) {
  const { activeWorkspaceId } = useContext(WorkspaceContext) || {};
  const { data: usageData } = useWorkspaceUsage(activeWorkspaceId);

  // State for 402 insufficient entitlements (consumables)
  const [errorState, setErrorState] = useState({
    open: false,
    entitlementType: null,
    required: 0,
    available: 0,
  });

  // State for 403 cap limit errors (max_ai_participants, max_seats)
  const [capLimitState, setCapLimitState] = useState({
    open: false,
    limitType: null,
    current: 0,
    max: 0,
    accountRep: null,
  });

  /**
   * Show the insufficient entitlements modal.
   * Call this when an API returns a 402 INSUFFICIENT error.
   *
   * @param {object} params - Error details
   * @param {string} params.entitlementType - Type of entitlement that was insufficient
   * @param {number} params.required - Amount required for the action
   * @param {number} params.available - Amount currently available
   */
  const showInsufficientEntitlementsError = useCallback((params) => {
    setErrorState({
      open: true,
      entitlementType: params.entitlementType,
      required: params.required,
      available: params.available,
    });
  }, []);

  /**
   * Hide the insufficient entitlements modal.
   */
  const hideInsufficientEntitlementsError = useCallback(() => {
    setErrorState((prev) => ({ ...prev, open: false }));
  }, []);

  /**
   * Show the cap limit reached modal.
   * Call this when an API returns a 403 MAX_AI_PARTICIPANTS_REACHED or MAX_SEATS_REACHED error.
   *
   * @param {object} params - Error details
   * @param {string} params.limitType - Type of limit reached (MAX_AI_PARTICIPANTS_REACHED, MAX_SEATS_REACHED)
   * @param {number} params.current - Current count
   * @param {number} params.max - Maximum allowed
   * @param {object} params.accountRep - Account representative info from error response
   */
  const showCapLimitError = useCallback((params) => {
    setCapLimitState({
      open: true,
      limitType: params.limitType,
      current: params.current,
      max: params.max,
      accountRep: params.accountRep,
    });
  }, []);

  /**
   * Hide the cap limit reached modal.
   */
  const hideCapLimitError = useCallback(() => {
    setCapLimitState((prev) => ({ ...prev, open: false }));
  }, []);

  /**
   * Handle API error responses.
   * Call this in API error handlers to automatically show the modal for 402/403 errors.
   *
   * Supports two error formats:
   * 1. Original axios format: error.response.status, error.response.data
   * 2. Transformed format (from axios interceptor): error.status, error.error_code, error.detail
   *
   * Handles:
   * - 402 INSUFFICIENT errors (consumable entitlements)
   * - 403 MAX_AI_PARTICIPANTS_REACHED errors (cap limits)
   * - 403 MAX_SEATS_REACHED errors (cap limits)
   *
   * @param {Error} error - The error from the API call
   * @returns {boolean} - True if the error was handled (was a 402/403 entitlement error), false otherwise
   */
  const handleApiError = useCallback(
    (error) => {
      // Check for transformed error format (from axios interceptor)
      // Format: { status: 402, error_code: 'INSUFFICIENT', detail: {...} }
      if (error?.status === 402 && error?.error_code === 'INSUFFICIENT' && error?.detail) {
        showInsufficientEntitlementsError({
          entitlementType: error.detail.entitlement_type,
          required: error.detail.required,
          available: error.detail.available,
        });
        return true;
      }

      // Check for original axios error format
      // Format: { response: { status: 402, data: { error_code: 'INSUFFICIENT', detail: {...} } } }
      if (error?.response?.status === 402) {
        const data = error.response.data;
        if (data?.error_code === 'INSUFFICIENT' && data?.detail) {
          showInsufficientEntitlementsError({
            entitlementType: data.detail.entitlement_type,
            required: data.detail.required,
            available: data.detail.available,
          });
          return true;
        }
      }

      // Check for 403 cap limit errors (MAX_AI_PARTICIPANTS_REACHED, MAX_SEATS_REACHED)
      // Format: { response: { status: 403, data: { error_code: '...', detail: {...}, account_rep: {...} } } }
      if (error?.response?.status === 403) {
        const data = error.response.data;
        if (
          data?.error_code === 'MAX_AI_PARTICIPANTS_REACHED' ||
          data?.error_code === 'MAX_SEATS_REACHED'
        ) {
          showCapLimitError({
            limitType: data.error_code,
            current: data.detail?.current || 0,
            max: data.detail?.max || 0,
            accountRep: data.account_reps || (data.account_rep ? [data.account_rep] : []),
          });
          return true;
        }
      }

      return false;
    },
    [showInsufficientEntitlementsError, showCapLimitError],
  );

  const accountReps = usageData?.account_representatives || [];
  const fallbackReps =
    accountReps.length > 0
      ? accountReps
      : usageData?.account_representative
        ? [usageData.account_representative]
        : [];

  return (
    <EntitlementContext.Provider
      value={{
        showInsufficientEntitlementsError,
        hideInsufficientEntitlementsError,
        showCapLimitError,
        hideCapLimitError,
        handleApiError,
      }}
    >
      {children}
      <InsufficientEntitlementsModal
        open={errorState.open}
        onClose={hideInsufficientEntitlementsError}
        entitlementType={errorState.entitlementType}
        required={errorState.required}
        available={errorState.available}
        accountRep={fallbackReps[0]}
      />
      <CapLimitReachedModal
        open={capLimitState.open}
        onClose={hideCapLimitError}
        limitType={capLimitState.limitType}
        current={capLimitState.current}
        max={capLimitState.max}
        accountRep={(capLimitState.accountRep && capLimitState.accountRep[0]) || fallbackReps[0]}
      />
    </EntitlementContext.Provider>
  );
}

/**
 * Hook to access entitlement error handling functions.
 *
 * Usage in API calls:
 * ```
 * const { handleApiError } = useEntitlementError();
 *
 * try {
 *   await runAnalysis();
 * } catch (error) {
 *   if (!handleApiError(error)) {
 *     // Handle other errors
 *     toast.error('Something went wrong');
 *   }
 * }
 * ```
 */
function useEntitlementError() {
  const context = useContext(EntitlementContext);
  if (!context) {
    throw new Error('useEntitlementError must be used within an EntitlementProvider');
  }
  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export { EntitlementContext, EntitlementProvider, useEntitlementError };
